import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  Button,
  Avatar,
  Paper,
  //TextField,
  //IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  FileText as FileTextIcon,
  Code as TechIcon,
  Rocket as ProductIcon,
  BarChart as MarketIcon,
  DollarSign as CostIcon,
  Target as TargetIcon,
  Lightbulb as VisionIcon,
  TrendingUp as KPIIcon,
  CheckCircle as ObjectiveIcon,
  Users as UserResearchIcon,
  GitBranch as RoadmapIcon,
  Package as FeaturesIcon,
  Zap as AgileIcon,
  Database as BackendIcon,
  Layout as FrontendIcon,
  Cloud as InfraIcon,
  Shield as SecurityIcon,
  Gauge as PerformanceIcon,
  GitBranch as DevOpsIcon,
  TrendingUp as GrowthIcon,
  PieChart as CompetitionIcon,
  Globe as ChannelIcon,
  Award as PositioningIcon,
  Crosshair as SegmentationIcon,
  Users2 as TargetMarketIcon,
  // Edit as EditIcon,
  //Cancel as CancelIcon,
  //Save as SaveIcon,
  //PlayArrow as PlayArrowIcon,
} from 'lucide-react';
import HolographicStepper, { StepData } from '../common/HolographicStepper.tsx';
import GradientText from '../common/GradientText.tsx';
import EditableContent from '../proposal/EditableContent.tsx';
import BackButton from '../common/BackButton.tsx';

interface ExecutiveStepperProps {
  executives: any[];
  problemStatement?: string;
  problemText?: string;
  setProblemText?: (text: string) => void;
  problemTitle?: string;
  handleStepperComplete?: () => void;
  initialStep?: number;
  onComplete?: () => void;
  onBack?: () => void;
  onViewDetailedPlan?: (planType: string) => void;
  handleViewProposal?: (id: string) => void;
  proposals?: any[];
}

const StepperContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  overflow: 'hidden',
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(3),
}));

const CostEstimateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  background: 'rgba(0, 204, 153, 0.1)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  marginBottom: theme.spacing(2),
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '&:last-child': {
    marginBottom: 0,
  }
}));

const ContentCard = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  height: 'calc(100% - 120px)',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(26, 26, 46, 0.4)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(138, 43, 226, 0.3)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(138, 43, 226, 0.5)',
    },
  },
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    opacity: 1,
  },
}));

const ExecutiveAvatar = styled(Avatar)(({ theme }) => ({
  width: 60,
  height: 60,
  marginBottom: theme.spacing(1),
  backgroundColor: 'rgba(138, 43, 226, 0.7)',
  border: '2px solid #00FFFF',
  boxShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
}));

const ExecutiveBio = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.8)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(3),
}));

const ExecutiveStepper: React.FC<ExecutiveStepperProps> = ({
  executives = [],
  problemStatement = 'Define your problem here...',
  problemText,
  setProblemText,
  problemTitle,
  handleStepperComplete,
  initialStep = 0,
  onComplete,
  onBack,
  onViewDetailedPlan,
  handleViewProposal,
  proposals
}) => {
  const [activeStep, setActiveStep] = useState<number>(initialStep);
  
  // State for Executive Summary (CEO)
  const [executiveSummary, setExecutiveSummary] = useState({
    overview: {
      title: 'Plan Overview',
      content: problemStatement,
      icon: FileTextIcon,
    },
    costEstimate: {
      estimated: 0,
      solution: 0,
      actual: 0,
    },
    mission: {
      title: 'Mission & Vision',
      content: 'Define your mission and vision statements here...',
      icon: VisionIcon,
    },
    objectives: {
      title: 'Key Objectives',
      items: [
        { id: 1, content: 'Define first key objective...', icon: ObjectiveIcon },
        { id: 2, content: 'Define second key objective...', icon: ObjectiveIcon },
      ],
    },
    kpis: {
      title: 'Key Performance Indicators',
      items: [
        { id: 1, content: 'Define first KPI...', icon: KPIIcon },
        { id: 2, content: 'Define second KPI...', icon: KPIIcon },
      ],
    },
  });

  // State for Technology Roadmap (CTO)
  const [techRoadmap, setTechRoadmap] = useState({
    overview: {
      title: 'Technology Stack Overview',
      content: 'Define the overall technology architecture and stack...',
      icon: TechIcon,
      cost: 4500,
    },
    frontend: {
      title: 'Frontend Architecture',
      stack: [
        {
          id: 'ui',
          name: 'UI Framework',
          description: 'React with TypeScript for type-safe development',
          icon: FrontendIcon,
        },
        {
          id: 'state',
          name: 'State Management',
          description: 'Redux Toolkit with RTK Query for API integration',
          icon: FrontendIcon,
        },
      ],
      cost: 3800,
    },
    backend: {
      title: 'Backend Infrastructure',
      stack: [
        {
          id: 'api',
          name: 'API Layer',
          description: 'Node.js with Express and GraphQL',
          icon: BackendIcon,
        },
        {
          id: 'database',
          name: 'Database',
          description: 'PostgreSQL with Redis caching',
          icon: BackendIcon,
        },
      ],
      cost: 4200,
    },
    infrastructure: {
      title: 'Cloud Infrastructure',
      components: [
        {
          id: 'hosting',
          name: 'Hosting',
          description: 'AWS ECS with auto-scaling',
          icon: InfraIcon,
        },
        {
          id: 'storage',
          name: 'Storage',
          description: 'S3 for assets, RDS for databases',
          icon: InfraIcon,
        },
      ],
      cost: 5500,
    },
    security: {
      title: 'Security & Compliance',
      measures: [
        {
          id: 'auth',
          name: 'Authentication & Authorization',
          description: 'OAuth 2.0 with JWT tokens',
          icon: SecurityIcon,
        },
        {
          id: 'encryption',
          name: 'Data Encryption',
          description: 'AES-256 encryption at rest and in transit',
          icon: SecurityIcon,
        },
      ],
      cost: 3900,
    },
    performance: {
      title: 'Performance Optimization',
      strategies: [
        {
          id: 'caching',
          name: 'Caching Strategy',
          description: 'Multi-layer caching with CDN',
          icon: PerformanceIcon,
        },
        {
          id: 'optimization',
          name: 'Code Optimization',
          description: 'Automated performance monitoring and optimization',
          icon: PerformanceIcon,
        },
      ],
      cost: 3200,
    },
    devops: {
      title: 'DevOps & CI/CD',
      pipeline: [
        {
          id: 'ci',
          name: 'Continuous Integration',
          description: 'Automated testing and build pipeline',
          icon: DevOpsIcon,
        },
        {
          id: 'cd',
          name: 'Continuous Deployment',
          description: 'Automated deployment with rollback capability',
          icon: DevOpsIcon,
        },
      ],
      cost: 4100,
    },
  });

  // Add Product Roadmap state (CPO)
  const [productRoadmap, setProductRoadmap] = useState({
    vision: {
      title: 'Product Vision & Strategy',
      content: 'Define the product vision, goals, and strategic direction...',
      icon: ProductIcon,
      cost: 2800,
    },
    userResearch: {
      title: 'User Research & Personas',
      personas: [
        {
          id: 1,
          title: 'Primary Persona',
          description: 'Define primary user persona...',
          icon: UserResearchIcon,
        },
        {
          id: 2,
          title: 'Secondary Persona',
          description: 'Define secondary user persona...',
          icon: UserResearchIcon,
        },
      ],
      cost: 3400,
    },
    roadmap: {
      title: 'Product Roadmap',
      quarters: [
        {
          id: 'Q1',
          title: 'Foundation',
          objectives: [
            'Core platform development',
            'Basic functionality',
            'Initial testing',
          ],
          icon: RoadmapIcon,
        },
        {
          id: 'Q2',
          title: 'Expansion',
          objectives: [
            'Advanced features',
            'Integration capabilities',
            'Beta program',
          ],
          icon: RoadmapIcon,
        },
        {
          id: 'Q3',
          title: 'Refinement',
          objectives: [
            'Performance optimization',
            'User experience enhancements',
            'Market launch preparation',
          ],
          icon: RoadmapIcon,
        },
        {
          id: 'Q4',
          title: 'Scaling',
          objectives: [
            'Enterprise features',
            'Advanced analytics',
            'Market expansion',
          ],
          icon: RoadmapIcon,
        },
      ],
      cost: 3800,
    },
    features: {
      title: 'Feature Prioritization',
      categories: [
        {
          id: 'must-have',
          title: 'Must-Have Features',
          items: ['Feature 1', 'Feature 2'],
          icon: FeaturesIcon,
        },
        {
          id: 'high-value',
          title: 'High-Value Features',
          items: ['Feature 3', 'Feature 4'],
          icon: FeaturesIcon,
        },
      ],
      cost: 3200,
    },
    agile: {
      title: 'Agile Development Process',
      processes: [
        {
          id: 'sprints',
          title: 'Sprint Planning',
          description: 'Two-week sprint cycles with defined goals...',
          icon: AgileIcon,
        },
        {
          id: 'ceremonies',
          title: 'Agile Ceremonies',
          description: 'Daily standups, sprint reviews, and retrospectives...',
          icon: AgileIcon,
        },
      ],
      cost: 2600,
    },
  });

  // Add Market Strategy & Analysis state (CMO)
  const [marketStrategy, setMarketStrategy] = useState({
    overview: {
      title: 'Market Strategy Overview',
      content: 'Define the overall market approach and positioning...',
      icon: MarketIcon,
      cost: 4200,
    },
    positioning: {
      title: 'Market Positioning',
      strategies: [
        {
          id: 'value',
          name: 'Value Proposition',
          description: 'Unique value offering in the market...',
          icon: PositioningIcon,
        },
        {
          id: 'differentiation',
          name: 'Differentiation Strategy',
          description: 'Key differentiators from competitors...',
          icon: PositioningIcon,
        },
      ],
      cost: 3500,
    },
    channels: {
      title: 'Marketing Channels',
      strategies: [
        {
          id: 'digital',
          name: 'Digital Marketing',
          description: 'Online presence and digital campaigns...',
          icon: ChannelIcon,
        },
        {
          id: 'traditional',
          name: 'Traditional Marketing',
          description: 'Offline marketing strategies...',
          icon: ChannelIcon,
        },
      ],
      cost: 5200,
    },
    marketAnalysis: {
      overview: {
        title: 'Market Analysis Overview',
        content: 'Comprehensive market research and insights...',
        icon: GrowthIcon,
        cost: 4800,
      },
      targetMarket: {
        title: 'Target Market Analysis',
        segments: [
          {
            id: 'primary',
            name: 'Primary Market',
            description: 'Core customer segment analysis...',
            size: '5M users',
            growth: '15% YoY',
            icon: TargetMarketIcon,
          },
          {
            id: 'secondary',
            name: 'Secondary Market',
            description: 'Additional market opportunities...',
            size: '3M users',
            growth: '12% YoY',
            icon: TargetMarketIcon,
          },
        ],
        cost: 3800,
      },
      competition: {
        title: 'Competitive Analysis',
        competitors: [
          {
            id: 'direct',
            name: 'Direct Competitors',
            description: 'Primary market competitors...',
            analysis: 'Strengths and weaknesses...',
            icon: CompetitionIcon,
          },
          {
            id: 'indirect',
            name: 'Indirect Competitors',
            description: 'Secondary market competitors...',
            analysis: 'Market overlap and differentiation...',
            icon: CompetitionIcon,
          },
        ],
        cost: 4200,
      },
      segmentation: {
        title: 'Market Segmentation',
        segments: [
          {
            id: 'demographic',
            name: 'Demographic Segmentation',
            description: 'Age, income, location analysis...',
            icon: SegmentationIcon,
          },
          {
            id: 'behavioral',
            name: 'Behavioral Segmentation',
            description: 'Usage patterns and preferences...',
            icon: SegmentationIcon,
          },
        ],
        cost: 3600,
      },
    },
  });

  // Add Financial Plan state (CFO)
  const [financialPlan, setFinancialPlan] = useState({
    overview: {
      title: 'Financial Overview',
      content: 'Financial strategy and projections overview...',
      icon: CostIcon,
      cost: 5500,
    },
    revenue: {
      title: 'Revenue Projections',
      projections: [
        {
          id: 'year1',
          period: 'Year 1',
          amount: 1000000,
          growth: '25%',
          description: 'Initial market penetration and customer acquisition...',
          icon: KPIIcon,
        },
        {
          id: 'year2',
          period: 'Year 2',
          amount: 2500000,
          growth: '150%',
          description: 'Market expansion and product scaling...',
          icon: KPIIcon,
        },
      ],
      cost: 3800,
    },
    costs: {
      title: 'Cost Structure',
      categories: [
        {
          id: 'fixed',
          name: 'Fixed Costs',
          items: [
            { name: 'Salaries', amount: 500000 },
            { name: 'Infrastructure', amount: 200000 },
          ],
          icon: CostIcon,
        },
        {
          id: 'variable',
          name: 'Variable Costs',
          items: [
            { name: 'Marketing', amount: 300000 },
            { name: 'Operations', amount: 250000 },
          ],
          icon: CostIcon,
        },
      ],
      cost: 4200,
    },
    funding: {
      title: 'Funding Requirements',
      rounds: [
        {
          id: 'seed',
          name: 'Seed Round',
          amount: 1000000,
          description: 'Initial product development and market entry...',
          icon: CostIcon,
        },
        {
          id: 'seriesA',
          name: 'Series A',
          amount: 5000000,
          description: 'Scaling operations and market expansion...',
          icon: CostIcon,
        },
      ],
      cost: 3500,
    },
    metrics: {
      title: 'Financial Metrics',
      kpis: [
        {
          id: 'burn',
          name: 'Burn Rate',
          value: '$100k/month',
          description: 'Current operational expenses...',
          icon: KPIIcon,
        },
        {
          id: 'runway',
          name: 'Runway',
          value: '18 months',
          description: 'Available operating time with current funding...',
          icon: KPIIcon,
        },
      ],
      cost: 2800,
    },
  });

  // Handler for generating content with AI
  const handleGenerate = async (section: string) => {
    console.log(`Generating content for ${section}`);
    // Implement AI generation logic here
  };

  // Content components for each executive
  const CEOContent = () => (
    <Section>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ExecutiveAvatar>
          {executives[0]?.name?.charAt(0) || 'C'}
        </ExecutiveAvatar>
        <Box sx={{ ml: 2 }}>
          <Typography variant="h5" sx={{ color: '#fff' }}>
            {executives[0]?.name || 'CEO'}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Chief Executive Officer
          </Typography>
        </Box>
      </Box>

      <ExecutiveBio>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
          {executives[0]?.bio || "Experienced CEO with a track record of driving innovation and growth."}
        </Typography>
      </ExecutiveBio>

      <GradientText gradient="primary" variant="h5" sx={{ mb: 2 }}>
        Executive Summary
      </GradientText>

      <Section>
        {/* Plan Overview */}
        <EditableContent
          title={executiveSummary.overview.title}
          content={executiveSummary.overview.content}
          icon={executiveSummary.overview.icon}
          onUpdate={(newData) => {
            setExecutiveSummary(prev => ({
              ...prev,
              overview: { ...prev.overview, ...newData },
            }));
          }}
          onGenerate={() => handleGenerate('overview')}
        />
      </Section>
      <Section>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Button
              variant="outlined"
              onClick={() => handleViewDetailedPlan('strategic')}
              sx={{ 
                mt: 2,
                color: '#00CC99',
                borderColor: 'rgba(0, 255, 255, 0.8)',
              }}
            >
              View Full Strategic Plan
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              variant="outlined"
              onClick={() => handleViewDetailedPlan('business')}
              sx={{ 
                mt: 2,
                color: '#00CC99',
                borderColor: 'rgba(0, 255, 255, 0.8)',
              }}
            >
              View Full Business Plan
            </Button>
          </Grid>
        </Grid>
      </Section>
      <Section>
        {/* Cost Estimates */}
        <CostEstimateBox>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <EditableContent
                title="Cost Estimate"
                content={`$${executiveSummary.costEstimate.estimated.toLocaleString()}`}
                icon={CostIcon}
                onUpdate={(newData) => {
                  setExecutiveSummary(prev => ({
                    ...prev,
                    costEstimate: { 
                      ...prev.costEstimate, 
                      estimated: Number(newData.content?.replace(/[^0-9.-]+/g, '') || 0) 
                    },
                  }));
                }}
                onGenerate={() => handleGenerate('costEstimate')}
              />
            </Grid>
            <Grid item xs={4}>
              <EditableContent
                title="Solution Estimate"
                content={`$${executiveSummary.costEstimate.solution.toLocaleString()}`}
                icon={TargetIcon}
                onUpdate={(newData) => {
                  setExecutiveSummary(prev => ({
                    ...prev,
                    costEstimate: { 
                      ...prev.costEstimate, 
                      solution: Number(newData.content?.replace(/[^0-9.-]+/g, '') || 0) 
                    },
                  }));
                }}
                onGenerate={() => handleGenerate('solutionEstimate')}
              />
            </Grid>
            <Grid item xs={4}>
              <EditableContent
                title="Actual Cost"
                content={`$${executiveSummary.costEstimate.actual.toLocaleString()}`}
                icon={CostIcon}
                onUpdate={(newData) => {
                  setExecutiveSummary(prev => ({
                    ...prev,
                    costEstimate: { 
                      ...prev.costEstimate, 
                      actual: Number(newData.content?.replace(/[^0-9.-]+/g, '') || 0) 
                    },
                  }));
                }}
              />
            </Grid>
          </Grid>
        </CostEstimateBox>
      </Section>

      <Section>
        {/* Mission & Vision */}
        <EditableContent
          title={executiveSummary.mission.title}
          content={executiveSummary.mission.content}
          icon={executiveSummary.mission.icon}
          onUpdate={(newData) => {
            setExecutiveSummary(prev => ({
              ...prev,
              mission: { ...prev.mission, ...newData },
            }));
          }}
          onGenerate={() => handleGenerate('mission')}
        />
      </Section>

      <Section>
        {/* Key Objectives */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Key Objectives
          </Typography>
          <List>
            {executiveSummary.objectives.items.map((objective) => (
              <ListItem key={objective.id}>
                <EditableContent
                  title={`Objective ${objective.id}`}
                  content={objective.content}
                  icon={objective.icon}
                  onUpdate={(newData) => {
                    setExecutiveSummary(prev => ({
                      ...prev,
                      objectives: {
                        ...prev.objectives,
                        items: prev.objectives.items.map(item =>
                          item.id === objective.id ? { ...item, ...newData } : item
                        ),
                      },
                    }));
                  }}
                  onGenerate={() => handleGenerate(`objective_${objective.id}`)}
                />
              </ListItem>
            ))}
          </List>
        </ContentBox>
      </Section>

      <Section>
        {/* KPIs */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Key Performance Indicators
          </Typography>
          <List>
            {executiveSummary.kpis.items.map((kpi) => (
              <ListItem key={kpi.id}>
                <EditableContent
                  title={`KPI ${kpi.id}`}
                  content={kpi.content}
                  icon={kpi.icon}
                  onUpdate={(newData) => {
                    setExecutiveSummary(prev => ({
                      ...prev,
                      kpis: {
                        ...prev.kpis,
                        items: prev.kpis.items.map(item =>
                          item.id === kpi.id ? { ...item, ...newData } : item
                        ),
                      },
                    }));
                  }}
                  onGenerate={() => handleGenerate(`kpi_${kpi.id}`)}
                />
              </ListItem>
            ))}
          </List>
        </ContentBox>
      </Section>
    </Section>
  );

  const CTOContent = () => (
    <Section>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ExecutiveAvatar>
          {executives[1]?.name?.charAt(0) || 'C'}
        </ExecutiveAvatar>
        <Box sx={{ ml: 2 }}>
          <Typography variant="h5" sx={{ color: '#fff' }}>
            {executives[1]?.name || 'CTO'}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Chief Technology Officer
          </Typography>
        </Box>
      </Box>

      <ExecutiveBio>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
          {executives[1]?.bio || "Technical leader focused on architecture and innovation."}
        </Typography>
      </ExecutiveBio>

      <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
        Technology Roadmap
      </GradientText>

      {/* Technology Stack Overview */}
      <EditableContent
        title={techRoadmap.overview.title}
        content={techRoadmap.overview.content}
        icon={techRoadmap.overview.icon}
        cost={techRoadmap.overview.cost}
        onUpdate={(newData) => {
          setTechRoadmap(prev => ({
            ...prev,
            overview: { ...prev.overview, ...newData },
          }));
        }}
        onGenerate={() => handleGenerate('techOverview')}
      />
      <Section>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Button
              variant="outlined"
              onClick={() => handleViewDetailedPlan('technology')}
              sx={{ 
                mt: 2,
                color: '#00CC99',
                borderColor: 'rgba(0, 255, 255, 0.8)',
              }}
            >
              View Full Technology Roadmap
            </Button>
          </Grid>
        </Grid>
      </Section>
      {/* Frontend Architecture */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Frontend Architecture
        </Typography>
        <Grid container spacing={2}>
          {techRoadmap.frontend.stack.map((item) => (
            <Grid item xs={12} md={6} key={item.id}>
              <EditableContent
                title={item.name}
                content={item.description}
                icon={item.icon}
                onUpdate={(newData) => {
                  setTechRoadmap(prev => ({
                    ...prev,
                    frontend: {
                      ...prev.frontend,
                      stack: prev.frontend.stack.map(s =>
                        s.id === item.id ? { ...s, ...newData } : s
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`frontend_${item.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Backend Infrastructure */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Backend Infrastructure
        </Typography>
        <Grid container spacing={2}>
          {techRoadmap.backend.stack.map((item) => (
            <Grid item xs={12} md={6} key={item.id}>
              <EditableContent
                title={item.name}
                content={item.description}
                icon={item.icon}
                onUpdate={(newData) => {
                  setTechRoadmap(prev => ({
                    ...prev,
                    backend: {
                      ...prev.backend,
                      stack: prev.backend.stack.map(s =>
                        s.id === item.id ? { ...s, ...newData } : s
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`backend_${item.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Cloud Infrastructure */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Cloud Infrastructure
        </Typography>
        <Grid container spacing={2}>
          {techRoadmap.infrastructure.components.map((item) => (
            <Grid item xs={12} md={6} key={item.id}>
              <EditableContent
                title={item.name}
                content={item.description}
                icon={item.icon}
                onUpdate={(newData) => {
                  setTechRoadmap(prev => ({
                    ...prev,
                    infrastructure: {
                      ...prev.infrastructure,
                      components: prev.infrastructure.components.map(c =>
                        c.id === item.id ? { ...c, ...newData } : c
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`infrastructure_${item.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Security & Compliance */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Security & Compliance
        </Typography>
        {techRoadmap.security.measures.map((measure) => (
          <Box key={measure.id} sx={{ mb: 2 }}>
            <EditableContent
              title={measure.name}
              content={measure.description}
              icon={measure.icon}
              onUpdate={(newData) => {
                setTechRoadmap(prev => ({
                  ...prev,
                  security: {
                    ...prev.security,
                    measures: prev.security.measures.map(m =>
                      m.id === measure.id ? { ...m, ...newData } : m
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`security_${measure.id}`)}
            />
          </Box>
        ))}
      </ContentBox>
    </Section>
  );

  const CPOContent = () => (
    <Section>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ExecutiveAvatar>
          {executives[2]?.name?.charAt(0) || 'C'}
        </ExecutiveAvatar>
        <Box sx={{ ml: 2 }}>
          <Typography variant="h5" sx={{ color: '#fff' }}>
            {executives[2]?.name || 'CPO'}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Chief Product Officer
          </Typography>
        </Box>
      </Box>

      <ExecutiveBio>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
          {executives[2]?.bio || "Product visionary focused on user-centered design and market fit."}
        </Typography>
      </ExecutiveBio>

      <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
        Product Roadmap
      </GradientText>

      {/* Product Vision & Strategy */}
      <EditableContent
        title={productRoadmap.vision.title}
        content={productRoadmap.vision.content}
        icon={productRoadmap.vision.icon}
        cost={productRoadmap.vision.cost}
        onUpdate={(newData) => {
          setProductRoadmap(prev => ({
            ...prev,
            vision: { ...prev.vision, ...newData },
          }));
        }}
        onGenerate={() => handleGenerate('productVision')}
      />
      <Section>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Button
              variant="outlined"
              onClick={() => handleViewDetailedPlan('product')}
              sx={{ 
                mt: 2,
                color: '#00CC99',
                borderColor: 'rgba(0, 255, 255, 0.8)',
              }}
            >
              View Full Product Roadmap
            </Button>
          </Grid>
        </Grid>
      </Section>
      
      <Section>
      {/* User Research & Personas */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          User Research & Personas
        </Typography>
        <Grid container spacing={2}>
          {productRoadmap.userResearch.personas.map((persona) => (
            <Grid item xs={12} sm={12} md={6} key={persona.id}>
              <EditableContent
                title={persona.title}
                content={persona.description}
                icon={persona.icon}
                onUpdate={(newData) => {
                  setProductRoadmap(prev => ({
                    ...prev,
                    userResearch: {
                      ...prev.userResearch,
                      personas: prev.userResearch.personas.map(p =>
                        p.id === persona.id ? { ...p, ...newData } : p
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`persona_${persona.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Quarterly Roadmap */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Quarterly Roadmap
        </Typography>
        <Grid container spacing={2}>
          {productRoadmap.roadmap.quarters.map((quarter) => (
            <Grid item xs={12} md={3} key={quarter.id}>
              <EditableContent
                title={`${quarter.id}: ${quarter.title}`}
                content={quarter.objectives.join('\n')}
                icon={quarter.icon}
                onUpdate={(newData) => {
                  setProductRoadmap(prev => ({
                    ...prev,
                    roadmap: {
                      ...prev.roadmap,
                      quarters: prev.roadmap.quarters.map(q =>
                        q.id === quarter.id ? {
                          ...q,
                          title: newData.title?.split(': ')[1] || q.title,
                          objectives: newData.content?.split('\n') || q.objectives,
                        } : q
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`quarter_${quarter.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>
      </Section>
    </Section>
  );

  const CMOContent = () => (
    <Section>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ExecutiveAvatar>
          {executives[3]?.name?.charAt(0) || 'C'}
        </ExecutiveAvatar>
        <Box sx={{ ml: 2 }}>
          <Typography variant="h5" sx={{ color: '#fff' }}>
            {executives[3]?.name || 'CMO'}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Chief Marketing Officer
          </Typography>
        </Box>
      </Box>

      <ExecutiveBio>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
          {executives[3]?.bio || "Marketing strategist with expertise in brand development and market positioning."}
        </Typography>
      </ExecutiveBio>

      <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
        Market Strategy & Analysis
      </GradientText>

      {/* Market Strategy Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ color: '#00CC99', mb: 2 }}>
          Market Strategy
        </Typography>

        {/* Strategy Overview */}
        <EditableContent
          title={marketStrategy.overview.title}
          content={marketStrategy.overview.content}
          icon={marketStrategy.overview.icon}
          cost={marketStrategy.overview.cost}
          onUpdate={(newData) => {
            setMarketStrategy(prev => ({
              ...prev,
              overview: { ...prev.overview, ...newData },
            }));
          }}
          onGenerate={() => handleGenerate('marketStrategyOverview')}
        />
        <Section>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Button
                variant="outlined"
                onClick={() => handleViewDetailedPlan('marketing')}
                sx={{ 
                  mt: 2,
                  color: '#00CC99',
                  borderColor: 'rgba(0, 255, 255, 0.8)',
                }}
              >
                View Full Marketing Plan
              </Button>
            </Grid>
          </Grid>
        </Section>

        <Section>
        {/* Market Positioning */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Market Positioning
          </Typography>
          <Grid container spacing={2}>
            {marketStrategy.positioning.strategies.map((strategy) => (
              <Grid item xs={12} md={6} key={strategy.id}>
                <EditableContent
                  title={strategy.name}
                  content={strategy.description}
                  icon={strategy.icon}
                  onUpdate={(newData) => {
                    setMarketStrategy(prev => ({
                      ...prev,
                      positioning: {
                        ...prev.positioning,
                        strategies: prev.positioning.strategies.map(s =>
                          s.id === strategy.id ? { ...s, ...newData } : s
                        ),
                      },
                    }));
                  }}
                  onGenerate={() => handleGenerate(`positioning_${strategy.id}`)}
                />
              </Grid>
            ))}
          </Grid>
        </ContentBox>
      </Section>
      </Box>
    </Section>
  );

  const CFOContent = () => (
    <Section>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ExecutiveAvatar>
          {executives[4]?.name?.charAt(0) || 'C'}
        </ExecutiveAvatar>
        <Box sx={{ ml: 2 }}>
          <Typography variant="h5" sx={{ color: '#fff' }}>
            {executives[4]?.name || 'CFO'}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Chief Financial Officer
          </Typography>
        </Box>
      </Box>

      <ExecutiveBio>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
          {executives[4]?.bio || "Financial expert specializing in strategic planning and resource allocation."}
        </Typography>
      </ExecutiveBio>

      <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
        Financial Plan
      </GradientText>

      {/* Financial Overview */}
      <EditableContent
        title={financialPlan.overview.title}
        content={financialPlan.overview.content}
        icon={financialPlan.overview.icon}
        cost={financialPlan.overview.cost}
        onUpdate={(newData) => {
          setFinancialPlan(prev => ({
            ...prev,
            overview: { ...prev.overview, ...newData },
          }));
        }}
        onGenerate={() => handleGenerate('financialOverview')}
      />

      <Section>
        <Grid container spacing={2}>
          <Grid item xs={4}>
          <Button
            variant="outlined"
            onClick={() => handleViewDetailedPlan('financial')}
            sx={{ 
              mt: 2,
              color: '#00CC99',
              borderColor: 'rgba(0, 255, 255, 0.8)',
            }}
          >
            View Full Financial Plan
            </Button>
          </Grid>
        </Grid>
      </Section>

      {/* Revenue Projections */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Revenue Projections
        </Typography>
        <Grid container spacing={2}>
          {financialPlan.revenue.projections.map((projection) => (
            <Grid item xs={12} md={6} key={projection.id}>
              <EditableContent
                title={projection.period}
                content={`Amount: $${projection.amount.toLocaleString()}\nGrowth: ${projection.growth}\n\n${projection.description}`}
                icon={projection.icon}
                onUpdate={(newData) => {
                  setFinancialPlan(prev => ({
                    ...prev,
                    revenue: {
                      ...prev.revenue,
                      projections: prev.revenue.projections.map(p =>
                        p.id === projection.id ? { ...p, ...newData } : p
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`revenue_${projection.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>
    </Section>
  );

  // Create Executive Steps
  const executiveSteps: StepData[] = [
    {
      label: 'CEO',
      description: 'Executive Summary & Strategy',
      icon: <FileTextIcon />,
      content: <CEOContent />
    },
    {
      label: 'CTO',
      description: 'Technology Roadmap',
      icon: <TechIcon />,
      content: <CTOContent />
    },
    {
      label: 'CPO',
      description: 'Product Roadmap',
      icon: <ProductIcon />,
      content: <CPOContent />
    },
    {
      label: 'CMO',
      description: 'Market Strategy & Analysis',
      icon: <MarketIcon />,
      content: <CMOContent />
    },
    {
      label: 'CFO',
      description: 'Financial Plan',
      icon: <CostIcon />,
      content: <CFOContent />
    }
  ];

  const handleViewDetailedPlan = (planType: string) => {
    if (onViewDetailedPlan) {
      onViewDetailedPlan(planType);
    }
  };

  return (
    <StepperContainer>
      {onBack && (
        <BackButton 
          onClick={onBack}
          destination="Workspace"
          tooltip="Return to the main workspace"
        />
      )}
      
      <Box sx={{ mb: 4, pb: 2, borderBottom: '1px solid rgba(138, 43, 226, 0.2)' }}>
        <HolographicStepper
          steps={executiveSteps}
          initialStep={initialStep}
          onComplete={onComplete}
          orientation="horizontal"
          alternativeLabel={true}
          onChange={(newStep) => setActiveStep(newStep)}
        />
      </Box>
      
      <Box sx={{ height: '20px' }} />
      
      <ContentCard>
        {executiveSteps[activeStep || initialStep].content}
      </ContentCard>
    </StepperContainer>
  );
};

export default ExecutiveStepper;